import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ref, onValue } from 'firebase/database';
import { X, Database, RefreshCw } from 'lucide-react';

// The bot sends a status report (botStatus) after every live check, every few minutes.
// If it stops arriving, the bot is treated as down.
const BOT_LATE_MINUTES = 12;
const BOT_DOWN_MINUTES = 30;

const LEVELS = {
  ok:      { color: '#22c55e', label: 'Operational' },
  warn:    { color: '#f59e0b', label: 'Degraded' },
  down:    { color: '#ef4444', label: 'Down' },
  pending: { color: '#71717a', label: 'Waiting' },
};
const RANK = { ok: 0, pending: 1, warn: 2, down: 3 };

// How healthy a regular check is: fine if it succeeded within ~2 of its intervals
function checkLevel(lastOk, everyMinutes, now) {
  if (!lastOk) return 'pending';
  const late = (now - lastOk) / 60000;
  if (late <= everyMinutes * 2 + 2) return 'ok';
  if (late <= everyMinutes * 4 + 5) return 'warn';
  return 'down';
}

export function useSiteStatus(database) {
  const [report, setReport] = useState(undefined);   // undefined = not loaded yet, null = never sent
  const [connected, setConnected] = useState(null);
  const [mountedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const offReport = onValue(ref(database, 'botStatus'), (snap) => setReport(snap.val()), () => setReport(null));
    const offConn = onValue(ref(database, '.info/connected'), (snap) => setConnected(!!snap.val()));
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => { offReport(); offConn(); clearInterval(tick); };
  }, [database]);

  const rows = [];

  // Discord bot: is the status report still arriving?
  const updatedAt = report && report.updatedAt;
  const age = updatedAt ? (now - updatedAt) / 60000 : Infinity;
  const botLevel = report === undefined ? 'pending' : age <= BOT_LATE_MINUTES ? 'ok' : age <= BOT_DOWN_MINUTES ? 'warn' : 'down';
  const botUp = botLevel === 'ok' || botLevel === 'warn';
  rows.push({
    name: report && report.botName ? `${report.botName} Discord bot` : 'Discord bot',
    level: botLevel,
    icon: '/discord.png',
    detail: report === undefined ? 'Checking…' : botLevel === 'ok' ? 'Online' : botLevel === 'warn' ? 'Running late' : 'Offline',
  });
  // Plain wording for the sync rows
  const syncDetail = (level) => ({ ok: 'Connected', warn: 'Delayed', down: 'Not syncing', pending: 'Waiting for the first check' })[level];

  // Everything below comes from the bot, so it's unknown while the bot is down
  const fromBot = (name, level, detail, icon) => rows.push(botUp ? { name, level, detail, icon } : { name, level: 'pending', detail: 'Unknown while the bot is offline', icon });

  const wn = (report && report.whatnot) || {};
  const live = (report && report.liveCheck) || {};
  const liveEvery = live.everyMinutes || 5;
  const wnLevel = wn.failing ? 'down' : checkLevel(wn.lastOk, liveEvery, now);
  fromBot('Whatnot live sync', wnLevel, wn.failing ? "Can't reach Whatnot" : syncDetail(wnLevel), '/wnlogo.png');

  const shop = (report && report.shopCheck) || {};
  const shopEvery = shop.everyMinutes || 15;
  const shopLevel = checkLevel(shop.lastOk, shopEvery, now);
  fromBot('Whatnot shop sync', shopLevel, syncDetail(shopLevel), '/wnlogo.png');

  // Live database: the website's own connection
  const connecting = !connected && now - mountedAt < 8000;
  rows.push({
    name: 'Live database',
    icon: 'database',
    level: connected ? 'ok' : connecting ? 'pending' : 'down',
    detail: connected ? 'Connected' : connecting ? 'Connecting…' : 'Disconnected, retrying',
  });

  // Features: from the bot, plus Toge Dash itself, which runs on this website and only needs the database
  const features = botUp ? [...((report && report.features) || [])] : [];
  if (features.length) {
    const at = features.findIndex((f) => f.name === 'Leaderboards');
    features.splice(at < 0 ? features.length : at, 0, { name: 'Toge Dash', ok: !!connected });
  }
  const overall = rows.reduce((worst, r) => (RANK[r.level] > RANK[worst] ? r.level : worst), 'ok');
  return { rows, features, overall, loading: report === undefined };
}

function Dot({ level, size = 8, pulse = false }) {
  const color = LEVELS[level].color;
  return (
    <span className="relative inline-flex flex-shrink-0" style={{ width: size, height: size }}>
      {pulse && <span className="absolute inset-0 rounded-full animate-ping" style={{ background: color, opacity: 0.5 }} />}
      <span className="relative rounded-full w-full h-full" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
    </span>
  );
}

export default function StatusPanel({ database }) {
  const [open, setOpen] = useState(false);
  const { rows, features, overall, loading } = useSiteStatus(database);
  const summary = loading ? 'Checking status…'
    : overall === 'ok' ? 'All systems operational'
    : overall === 'down' ? 'Something is down'
    : overall === 'warn' ? 'Some things are running slow'
    : 'Waiting for updates';

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={summary}
        aria-label={`Site status: ${summary}`}
        className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-2.5 py-1.5 transition-all hover:scale-105 active:scale-95"
      >
        <Dot level={loading ? 'pending' : overall} pulse={!loading && overall === 'ok'} />
        <span className="hidden sm:inline text-[11px] font-medium text-zinc-300 tracking-wide">Status</span>
        {/* dotted "i", so it's clear the pill can be clicked */}
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" className="text-zinc-400 flex-shrink-0">
          <circle cx="12" cy="12" r="10" strokeDasharray="0.01 3.93" strokeWidth="2.4" />
          <line x1="12" y1="11" x2="12" y2="16.5" />
          <circle cx="12" cy="7.5" r="0.4" fill="currentColor" strokeWidth="2.2" />
        </svg>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={() => setOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-700/70 rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-bold text-base">System Status</span>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 mb-4" style={{ background: `${LEVELS[loading ? 'pending' : overall].color}1a`, border: `1px solid ${LEVELS[loading ? 'pending' : overall].color}40` }}>
              <Dot level={loading ? 'pending' : overall} size={10} pulse={!loading && overall === 'ok'} />
              <span className="text-white text-sm font-semibold">{summary}</span>
            </div>

            <div className="flex flex-col divide-y divide-zinc-800">
              {rows.map((r) => (
                <div key={r.name} className="flex items-start gap-3 py-2.5">
                  <span className="mt-1.5"><Dot level={r.level} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-white text-sm font-medium">
                        {r.icon === 'database' ? (
                          // a tile the same size and shape as the Discord and Whatnot logos
                          <span className="flex-shrink-0 flex items-center justify-center" style={{ width: '14px', height: '14px', borderRadius: '4px', background: 'linear-gradient(to bottom, #38c4ff, #04aff7)' }}>
                            <Database className="text-white" style={{ width: '9px', height: '9px' }} strokeWidth={2.75} />
                          </span>
                        ) : r.icon && <img src={r.icon} alt="" className="flex-shrink-0 object-contain" style={{ width: '14px', height: '14px' }} />}
                        {r.name}
                      </span>
                      <span className="text-[11px] font-semibold" style={{ color: LEVELS[r.level].color }}>{LEVELS[r.level].label}</span>
                    </div>
                    <div className="text-zinc-400 text-xs mt-0.5">{r.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            {features.length > 0 && (
              <div className="mt-3 pt-3 border-t border-zinc-800">
                <div className="text-zinc-500 text-[10px] font-semibold uppercase tracking-wider mb-2 text-center">Bot features</div>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {features.map((f) => (
                    <span key={f.name} className="flex items-center gap-1.5 bg-zinc-800/80 rounded-full px-2 py-0.5 text-[11px] text-zinc-300">
                      <Dot level={f.ok ? 'ok' : 'down'} size={6} />
                      {f.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-1 text-zinc-600 text-[10px] mt-4">
              <RefreshCw style={{ width: '10px', height: '10px' }} strokeWidth={2.5} />
              Updates in real-time
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
